import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { userStore } from '../../store';
import { ImagePreviewModal, showToast, dynamicIsland } from '../../components/shared';
import { BASE_URL } from '../../database/apis';
import { User, Lock, HelpCircle, LogOut, ChevronRight, Mail, Phone, BookMarked, ExternalLink, AlertCircle } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const ProfileHubScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const [showImagePreview, setShowImagePreview] = useState(false);
  const user = userStore.user;

  // D2P returns absolute presigned URLs already
  const profileImageUrl = user?.photo || '';

  // Open the d2p web's author dashboard with the JWT in the URL so the
  // browser session is auto-established. window.open(url, '_system') makes
  // Capacitor route through the system browser on iOS/Android (App Store
  // safe) and falls back to a new tab on web.
  const openAuthorDashboard = () => {
    if (!userStore.authToken) return;
    const dest = userStore.requiresOnboarding
      ? '/d2p/authenticated-author/profile' // nudge to finish onboarding
      : '/d2p/authenticated-author/dashboard';
    const exchangeUrl = `${BASE_URL}api/d2p/auth/exchange?token=${encodeURIComponent(
      userStore.authToken
    )}&dest=${encodeURIComponent(dest)}`;
    try {
      window.open(exchangeUrl, '_system');
    } catch {
      // _system can throw on some webviews; plain new tab is fine on web.
      window.open(exchangeUrl, '_blank');
    }
    dynamicIsland.show({
      kind: 'info',
      title: 'Opening author dashboard',
      subtitle: userStore.requiresOnboarding
        ? 'Finish onboarding to start selling'
        : 'Manage your books on d2p',
    });
  };

  const handleLogout = () => {
    userStore.logout(() => {
      showToast('Logged out successfully', 'info');
      navigate('/app/home', { replace: true });
    });
  };

  if (!userStore.isLoggedIn) {
    return (
      <div className="h-full flex flex-col bg-background">
        <div className="safe-area-top bg-background">
          <div className="px-5 pt-4 pb-2">
            <h1 className="text-xl font-bold text-foreground">Profile</h1>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-8">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-4">
            <User className="w-8 h-8 text-muted-foreground/50" />
          </div>
          <p className="text-foreground text-base font-medium mb-1">Welcome to KleverBook</p>
          <p className="text-muted-foreground text-xs text-center mb-6">
            Sign in to manage your profile and settings
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => navigate('/login')}>
              Sign In
            </Button>
            <Button variant="outline" onClick={() => navigate('/signup')}>
              Sign Up
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const MenuItem = ({
    icon: Icon,
    label,
    sublabel,
    onClick,
  }: {
    icon: any;
    label: string;
    sublabel?: string;
    onClick: () => void;
  }) => (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 px-4 py-3.5 active:bg-muted transition-colors"
    >
      <div className="w-9 h-9 rounded-lg bg-secondary/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-secondary" />
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {sublabel && <p className="text-xs text-muted-foreground mt-0.5">{sublabel}</p>}
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </button>
  );

  return (
    <div className="h-full flex flex-col bg-background">
      <ImagePreviewModal
        visible={showImagePreview}
        imageUrl={profileImageUrl}
        onClose={() => setShowImagePreview(false)}
      />

      {/* Header */}
      <div className="safe-area-top bg-background">
        <div className="px-5 pt-4 pb-2">
          <h1 className="text-xl font-bold text-foreground">Profile</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Profile Card */}
        <div className="px-5 pt-2 pb-4">
          <Card className="p-5">
            <div className="flex items-center gap-4">
              <button onClick={() => profileImageUrl && setShowImagePreview(true)}>
                <Avatar className="w-16 h-16">
                  {profileImageUrl ? (
                    <AvatarImage src={profileImageUrl} alt={user?.name} />
                  ) : null}
                  <AvatarFallback className="text-lg font-semibold bg-secondary/10 text-secondary">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </button>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-foreground truncate">
                  {user?.name || 'User'}
                </h3>
                <div className="flex items-center gap-1.5 mt-1">
                  <Mail className="w-3 h-3 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                {user?.phone && (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Phone className="w-3 h-3 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">{user.phone}</p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Author Tools — only for users with an Author row in d2p */}
        {userStore.isAuthor && (
          <div className="px-5 pb-4">
            <Card className="overflow-hidden">
              <button
                onClick={openAuthorDashboard}
                className="w-full flex items-center gap-4 px-4 py-4 active:bg-muted transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-[#fdce03]/15 flex items-center justify-center">
                  <BookMarked className="w-5 h-5 text-[#fdce03]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">Author Tools</p>
                    {userStore.isApprovedAuthor ? (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-500/15 text-green-600">
                        Approved
                      </span>
                    ) : userStore.requiresOnboarding ? (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Setup needed
                      </span>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {userStore.requiresOnboarding
                      ? 'Finish your Stripe setup on d2p to start earning'
                      : 'Manage your books on d2p'}
                  </p>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
              </button>
            </Card>
          </div>
        )}

        {/* Menu Items */}
        <div className="px-5 pb-4">
          <Card className="overflow-hidden">
            <MenuItem
              icon={User}
              label="Edit Profile"
              sublabel="Update your personal information"
              onClick={() => navigate('/app/profile/edit')}
            />
            <Separator />
            <MenuItem
              icon={Lock}
              label="Change Password"
              sublabel="Update your account password"
              onClick={() => navigate('/app/profile/password')}
            />
            <Separator />
            <MenuItem
              icon={HelpCircle}
              label="Help & Support"
              sublabel="Get help and contact us"
              onClick={() => navigate('/app/profile/help')}
            />
          </Card>
        </div>

        {/* Logout */}
        <div className="px-5 pb-8">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Card className="overflow-hidden">
                <button className="w-full flex items-center gap-4 px-4 py-3.5 active:bg-muted transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center">
                    <LogOut className="w-4 h-4 text-destructive" />
                  </div>
                  <p className="text-sm font-medium text-destructive">Logout</p>
                </button>
              </Card>
            </AlertDialogTrigger>
            <AlertDialogContent className="mx-6 max-w-sm">
              <AlertDialogHeader>
                <AlertDialogTitle>Logout</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to logout? You'll need to sign in again to access your account.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleLogout}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Logout
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
});

export default ProfileHubScreen;
